import { Box, Button } from "@tm/components"
import { ArticleListSortingMode } from "@tm/models"
import { useCallback, useMemo, useRef } from "react"
import { useLocalization } from "@tm/localization"
import { ArticleGroup } from "../components/ArticleGroup"
import { NextPageLoader } from "../components/NextPageLoader"
import { NoSearchResult } from "../components/NoSearchResult"
import { ArticleSelection } from "../components/ArticleSelection"
import { useUniversalPartsListParamsContext } from "./ContextProvider/ListParams"
import { useErpInfosContext, useListOptionsContext } from "../ContextProvider"
import { useUniversalPartsArticlesContext } from "./ContextProvider/Articles"
import { useResizeErpColumnInDefaultArticleItem } from "../hooks/useResizeErpContainerColumn"
import { CrossSelling } from "../components/CrossSelling"
import { TileWrapper } from "../components/StyledComponents"

type Props = {
    articleSelectionRef: React.RefObject<HTMLDivElement>
}

export function UniversalPartsList(props: Props) {
    const { translateText } = useLocalization()

    const {
        sorting: { productGroups: sortingProductGroups, setForProductGroups },
        noResult,
        isFiltersLoading,
        startParams: { crossSellingModalType },
    } = useUniversalPartsListParamsContext()

    const {
        articleGroups,
        isLoading,
        isLoaded: articlesLoaded,
        isFetchingNextPage,
        hasNextPage,
        loadNextPage,
        isStalling,
        isFrontendFiltered,
        isEnabled,
        articles,
    } = useUniversalPartsArticlesContext()
    const { didErpInfoStartedLoading, isNoTeccomRequestAvailable, hasNoErpAreaIcons } = useErpInfosContext()

    const isLoaded = articlesLoaded && didErpInfoStartedLoading

    const { tileView, hideProductGroupHeader, hideAddToBasketButton } = useListOptionsContext()

    const mainContainerRef = useRef<HTMLElement>(null)
    useResizeErpColumnInDefaultArticleItem(mainContainerRef.current, !!articleGroups.length)

    const handleChangeSorting = useCallback(
        (productGroupId: number, value: ArticleListSortingMode | undefined) => {
            setForProductGroups([productGroupId], value)
        },
        [setForProductGroups]
    )

    const showPageLoader = ((hasNextPage || isLoading || isFiltersLoading) && !isFrontendFiltered) || isStalling || isFetchingNextPage || !isEnabled

    const skeletonArticleCount = useMemo(() => {
        if (tileView) {
            return articleGroups.length ? 1 : 30
        }
        return articleGroups.length ? 1 : 5
    }, [articleGroups.length, tileView])

    const renderArticleGroups = useCallback(() => {
        return articleGroups.map((group, groupIndex) => {
            const groupOffset = articleGroups.slice(0, groupIndex).reduce((acc, prevGroup) => acc + (prevGroup.articles?.length || 0), 0)
            const isLast = groupIndex === articleGroups.length - 1

            return (
                <ArticleGroup
                    groupOffset={groupOffset}
                    key={group.productGroup.id}
                    group={group}
                    currentSorting={sortingProductGroups[group.productGroup.id]}
                    onChangeSorting={handleChangeSorting}
                    showPageLoader={showPageLoader && isLast}
                    loadNextPage={loadNextPage}
                />
            )
        })
    }, [articleGroups, handleChangeSorting, loadNextPage, sortingProductGroups, showPageLoader])

    if (noResult) {
        return <NoSearchResult searchType="universal" />
    }

    return (
        <Box display="flex" flexDirection="column" position="relative" ref={mainContainerRef}>
            <ArticleSelection
                showArticleComparision
                canBeOpenedInModal
                hideBasketButton={hideAddToBasketButton}
                selectionRef={props.articleSelectionRef}
            />

            <CrossSelling
                sortChanged={sortingProductGroups}
                newSearchLoaded={isLoaded && !isStalling}
                crossSellingModalType={crossSellingModalType}
                articlesCount={articles?.length}
                frontEndFilterChanged={isFrontendFiltered}
            />
            {isLoaded &&
                !isStalling &&
                (hideProductGroupHeader ? (
                    <TileWrapper isNoTeccomRequestAvailable={isNoTeccomRequestAvailable} hasNoErpAreaIcons={hasNoErpAreaIcons}>
                        {renderArticleGroups()}
                        {showPageLoader && (
                            <NextPageLoader
                                canLoadNextArticles={hasNextPage}
                                skeletonArticleCount={skeletonArticleCount}
                                loadNextPage={loadNextPage}
                            />
                        )}
                    </TileWrapper>
                ) : (
                    renderArticleGroups()
                ))}

            {showPageLoader && (!tileView || !articleGroups.length) && (
                <NextPageLoader
                    loadNextPage={loadNextPage}
                    canLoadNextArticles={hasNextPage}
                    skeletonArticleCount={skeletonArticleCount}
                    skeletonCategory
                />
            )}

            {hasNextPage && isFrontendFiltered && (
                <Box order={999999} display="flex" justifyContent="center">
                    <Button color="highlight" onClick={loadNextPage}>
                        {translateText(12430)}
                    </Button>
                </Box>
            )}
        </Box>
    )
}
